// AUTO-GENERATED from weborder.json (OpenAPI). Do not edit by hand.
// Domain: rating
// Regenerate via scripts/gen-dtos.py

import { SpecialityFactorTypeResponse } from "./auth.dto.js";

/**
 * Anonymous rating calculation (`POST /api/weborder/v1/auth/calculate`).
 * Dates as strings **MM/dd/yyyy**. Optional `submissionid` / rating `id` are
 * cross-checked when present.
 */
export class RatingRequest {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {number} */
    this.id = raw.id ?? 0; // Existing single-rating id when recalculating in submission context.
    /** @type {string} */
    this.zipcode = raw.zipcode ?? ""; // ZIP code (5 digits).
    /** @type {string} */
    this.effectiveDate = raw.effectiveDate ?? ""; // Effective date **MM/dd/yyyy**.
    /** @type {string} */
    this.hoursPerWeek = raw.hoursPerWeek ?? ""; // Weekly hours as text (decimal hours, e.g. "40") or hours-worked lookup label, depending on...
    /** @type {number} */
    this.parttimeFulltimeFactor = raw.parttimeFulltimeFactor ?? 0; // Part-time/full-time factor resolved from the selected hours lookup.
    /** @type {number} */
    this.coverageLimitId = raw.coverageLimitId ?? 0; // Coverage ILF/DLF limit id (≥1 when sent).
    /** @type {number} */
    this.retroLimitId = raw.retroLimitId ?? 0; // Retro ILF/DLF limit id (≥1 when sent).
    /** @type {number} */
    this.specialtiesid = raw.specialtiesid ?? 0; // Speciality master id.
    /** @type {number} */
    this.year = raw.year ?? 0; // Policy year factor (legacy/int field).
    /** @type {string} */
    this.expiredDate = raw.expiredDate ?? ""; // Expiration date **MM/dd/yyyy**.
    /** @type {string} */
    this.retroCoverageDate = raw.retroCoverageDate ?? ""; // Retro coverage date **MM/dd/yyyy**.
    /** @type {number} */
    this.submissionid = raw.submissionid ?? 0; // Submission id when validating rating in context; must exist if sent.
  }
}

export class RatingResponseDTO {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {number} */
    this.total = raw.total ?? 0;
    /** @type {number} */
    this.premium = raw.premium ?? 0;
    /** @type {QuoteFormFeeResponse[]} */
    this.fees = Array.isArray(raw.fees) ? raw.fees : [];
    /** @type {QuoteFormFeeResponse[]} */
    this.taxes = Array.isArray(raw.taxes) ? raw.taxes : [];
  }
}

/**
 * Body for PUT /api/weborder/v1/insured/rating. Updates an existing single
 * rating on a submission for the authenticated insured. Insured id is taken
 * from the JWT, not from this payload. Dates use MM/dd/yyyy. Server validates
 * submission, rating id, zipcode, specialityDetailsId, ILF/DLF limits, and
 * date ordering before applying changes.
 */
export class UpdateRatingRequest {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {string} */
    this.zipcode = raw.zipcode ?? ""; // Practice or risk location ZIP code. Must be exactly 5 digits.
    /** @type {string} */
    this.effectiveDate = raw.effectiveDate ?? ""; // Policy effective date in MM/dd/yyyy format.
    /** @type {string} */
    this.expiredDate = raw.expiredDate ?? ""; // Policy expired date in MM/dd/yyyy format.
    /** @type {string} */
    this.retroCoverageDate = raw.retroCoverageDate ?? ""; // Policy retro date in MM/dd/yyyy format.
    /** @type {number} */
    this.coverageilfdlfid = raw.coverageilfdlfid ?? 0; // Coverage ILF/DLF identifier. Must be greater than 0.
    /** @type {number} */
    this.retroilfdlfid = raw.retroilfdlfid ?? 0; // Retro ILF/DLF identifier. Must be greater than 0.
    /** @type {string} */
    this.hoursperweek = raw.hoursperweek ?? ""; // Hours worked per week (decimal string or lookup id text).
    /** @type {number} */
    this.parttimeFulltimeFactor = raw.parttimeFulltimeFactor ?? 0; // Part time full time factor (decimal).
    /** @type {number} */
    this.specialityDetailsId = raw.specialityDetailsId ?? 0; // Speciality identifier. Must be greater than 0.
    /** @type {number} */
    this.claims = raw.claims ?? 0; // Number of claims associated. Can be 0 or more.
    /** @type {number} */
    this.singleRatingId = raw.singleRatingId ?? 0; // Single rating identifier. Must be greater than 0.
    /** @type {number} */
    this.submissionId = raw.submissionId ?? 0; // Submission identifier. Must be greater than 0.
  }
}

/**
 * ILF/DLF limits by zip and speciality (`POST
 * /api/weborder/v1/auth/ilf-dlf`). Optional `submissionId` must exist when
 * sent. Optional `effectiveDate` (**MM/dd/yyyy**).
 */
export class IlfDlfRequest {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {string} */
    this.zipcode = raw.zipcode ?? ""; // ZIP code (5 digits).
    /** @type {string} */
    this.effectiveDate = raw.effectiveDate ?? ""; // Optional rating context date **MM/dd/yyyy**.
    /** @type {number} */
    this.specialtiesid = raw.specialtiesid ?? 0; // Speciality master id.
    /** @type {number} */
    this.submissionId = raw.submissionId ?? 0; // Optional submission id; must exist when provided.
  }
}

export class IlfDlfAndYearListResponse {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {IlfDlfLookupResponse[]} */
    this.ilfDlfResponse = Array.isArray(raw.ilfDlfResponse) ? raw.ilfDlfResponse.map((x) => new IlfDlfLookupResponse(x)) : [];
    /** @type {YearResponse[]} */
    this.yearResponse = Array.isArray(raw.yearResponse) ? raw.yearResponse.map((x) => new YearResponse(x)) : [];
    /** @type {string} */
    this.state = raw.state ?? "";
    /** @type {string} */
    this.st = raw.st ?? "";
    /** @type {string} */
    this.county = raw.county ?? "";
    /** @type {SpecialityFactorTypeResponse[]} */
    this.surgeryList = Array.isArray(raw.surgeryList) ? raw.surgeryList.map((x) => new SpecialityFactorTypeResponse(x)) : [];
    /** @type {number} */
    this.total = raw.total ?? 0;
    /** @type {HoursWorkedFactorLookup[]} */
    this.hoursWorkedList = Array.isArray(raw.hoursWorkedList) ? raw.hoursWorkedList.map((x) => new HoursWorkedFactorLookup(x)) : [];
    /** @type {number} */
    this.defaultIlfDlf = raw.defaultIlfDlf ?? 0;
    /** @type {number} */
    this.defaultyear = raw.defaultyear ?? 0;
    /** @type {number} */
    this.defaultHoursWorked = raw.defaultHoursWorked ?? 0;
    /** @type {number} */
    this.defaultSurgery = raw.defaultSurgery ?? 0;
    /** @type {number} */
    this.defaultClaims = raw.defaultClaims ?? 0;
    /** @type {string} */
    this.city = raw.city ?? "";
    /** @type {string} */
    this.defense = raw.defense ?? "";
    /** @type {QuoteFormFeeResponse[]} */
    this.fees = Array.isArray(raw.fees) ? raw.fees : []; // Rating fees and policy fees as label/value pairs.
    /** @type {number} */
    this.feesTotal = raw.feesTotal ?? 0; // Sum of all `fees` values.
    /** @type {QuoteFormFeeResponse[]} */
    this.taxes = Array.isArray(raw.taxes) ? raw.taxes : []; // Taxes as label/value pairs.
    /** @type {number} */
    this.taxTotal = raw.taxTotal ?? 0; // Sum of all `taxes` values.
  }
}

export class IlfDlfLookupResponse {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {number} */
    this.id = raw.id ?? 0;
    /** @type {string} */
    this.currentprior = raw.currentprior ?? "";
    /** @type {string} */
    this.state = raw.state ?? "";
    /** @type {string} */
    this.limit = raw.limit ?? "";
    /** @type {number} */
    this.limitPerIncident = raw.limitPerIncident ?? 0;
    /** @type {number} */
    this.aggregateLimit = raw.aggregateLimit ?? 0;
    /** @type {number} */
    this.factor = raw.factor ?? 0;
    /** @type {boolean} */
    this.isdefault = raw.isdefault ?? false;
    /** @type {boolean} */
    this.isallowdelete = raw.isallowdelete ?? false;
  }
}

export class YearResponse {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {number} */
    this.id = raw.id ?? 0;
    /** @type {number} */
    this.year = raw.year ?? 0;
    /** @type {number} */
    this.domacileStateTaxRate = raw.domacileStateTaxRate ?? 0;
    /** @type {number} */
    this.stateTax = raw.stateTax ?? 0;
  }
}

export class HoursWorkedFactorLookup {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {number} */
    this.id = raw.id ?? 0;
    /** @type {string} */
    this.hoursWorked = raw.hoursWorked ?? "";
    /** @type {number} */
    this.factor = raw.factor ?? 0;
    /** @type {boolean} */
    this.default = raw.default ?? false;
    /** @type {boolean} */
    this.fullTime = raw.fullTime ?? false;
  }
}

export class QuoteFormData {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {string} */
    this.zipcode = raw.zipcode ?? "";
    /** @type {string} */
    this.state = raw.state ?? "";
    /** @type {string} */
    this.st = raw.st ?? "";
    /** @type {string} */
    this.county = raw.county ?? "";
    /** @type {number} */
    this.total = raw.total ?? 0;
    /** @type {number} */
    this.defaultIlfDlf = raw.defaultIlfDlf ?? 0;
    /** @type {number} */
    this.defaultyear = raw.defaultyear ?? 0;
    /** @type {number} */
    this.defaultParttimeFulltimeFactor = raw.defaultParttimeFulltimeFactor ?? 0;
    /** @type {number} */
    this.defaultSurgery = raw.defaultSurgery ?? 0;
    /** @type {number} */
    this.defaultClaims = raw.defaultClaims ?? 0;
    /** @type {string} */
    this.city = raw.city ?? "";
    /** @type {string} */
    this.defaultDefense = raw.defaultDefense ?? "";
    /** @type {string} */
    this.effectiveDate = raw.effectiveDate ?? ""; // Effective date **MM/dd/yyyy**.
    /** @type {string} */
    this.defaultIlfDlfName = raw.defaultIlfDlfName ?? "";
    /** @type {string} */
    this.defaultHoursWorkedName = raw.defaultHoursWorkedName ?? "";
    /** @type {number} */
    this.premium = raw.premium ?? 0;
    /** @type {QuoteFormFeeResponse[]} */
    this.fees = Array.isArray(raw.fees) ? raw.fees : []; // Rating fees and policy fees as label/value pairs.
    /** @type {number} */
    this.feesTotal = raw.feesTotal ?? 0; // Sum of all `fees` values.
    /** @type {QuoteFormFeeResponse[]} */
    this.taxes = Array.isArray(raw.taxes) ? raw.taxes : []; // Taxes as label/value pairs.
    /** @type {number} */
    this.taxTotal = raw.taxTotal ?? 0; // Sum of all `taxes` values.
    /** @type {string} */
    this.country = raw.country ?? "";
    /** @type {number} */
    this.specialtiesid = raw.specialtiesid ?? 0;
  }
}

/**
 * QuotesRequest limits by zip and speciality (`POST
 * /api/weborder/v1/auth/quotedata`).
 */
export class QuotesRequest {
  /** @param {object} [raw] */
  constructor(raw = {}) {
    /** @type {string} */
    this.zipcode = raw.zipcode ?? ""; // ZIP code (5 digits).
    /** @type {string} */
    this.effectiveDate = raw.effectiveDate ?? ""; // Optional rating context date **MM/dd/yyyy**.
    /** @type {number} */
    this.specialtiesMasterId = raw.specialtiesMasterId ?? 0; // Speciality master id.
  }
}
